import dayjs from 'dayjs'
import capitalize from 'lodash/capitalize'

import { currencyFormatter } from './AdjustPaymentModal.constants'
import {
  AdjustPaymentUnassignedTarget,
  GroupRows,
  GroupState,
  ManualFormState,
  PromisedAction,
} from './AdjustPaymentModal.types'
import {
  AllocationClassification,
  ManualRefundSource,
} from '../../api/reconciliation-v2-enums'

export const fmtMoney = (n: number) =>
  currencyFormatter.safeFormat(String(n), '$0.00')

/** Bare equation number (no $), en-US grouped, two decimals — per the design. */
export const bareFmt = (n: number) =>
  Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

/** Sanitize a currency keystroke to at most two decimals (empty allowed). */
export const isMoneyInput = (raw: string) =>
  raw === '' || /^\d*\.?\d{0,2}$/.test(raw)

/** Extract a human message from an Axios-shaped error, else 'unknown error'. */
export const extractApiErrorMessage = (err: unknown): string => {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof err.response === 'object' &&
    err.response !== null &&
    'data' in err.response &&
    typeof err.response.data === 'object' &&
    err.response.data !== null &&
    'message' in err.response.data &&
    typeof err.response.data.message === 'string'
  ) {
    return err.response.data.message
  }
  return 'unknown error'
}

/** Sum the numeric row amounts of a group (blank/invalid entries count as 0). */
export const sumRows = (rows: GroupRows): number =>
  Object.values(rows).reduce((s, v) => {
    const n = Number(v)
    return s + (Number.isFinite(n) ? n : 0)
  }, 0)

/**
 * Order-independent signature of the current allocation (benefit + period +
 * normalized amount for every funded row). Used to tell whether the operator
 * has actually changed the allocation versus what was loaded, so an unchanged
 * cell can't be re-applied as a no-op.
 */
export const allocationSignature = (groups: GroupState[]): string =>
  groups
    .filter((g) => !!g.benefit)
    .flatMap((g) =>
      Object.entries(g.rows)
        .filter(([, v]) => Number(v) > 0)
        .map(
          (entry) =>
            `${g.benefit?.benefitId}|${entry[0]}|${Number(entry[1]).toFixed(2)}`
        )
    )
    .sort()
    .join(',')

/**
 * Processor status/type of a source transaction, ready to render.
 *
 * `line` is the joined form the rail shows; `type` is kept separate so callers
 * can tell whether an authoritative processor type is present.
 */
export interface SourceTxLabels {
  status: string | null
  type: string | null
  line: string
}

/**
 * Labels for the rail's source transaction, or `null` when there is nothing to
 * show. Only unassigned targets carry these — cell and manual targets have no
 * source transaction — so those modes render nothing rather than empty fields.
 *
 * Both fields are capitalised for display. Note this is deliberately *not* the
 * same treatment as Payment Assignment's Transaction tab, which capitalises
 * status on one branch only and renders type raw.
 */
export const sourceTxLabels = (
  unassigned: AdjustPaymentUnassignedTarget | null
): SourceTxLabels | null => {
  const status = unassigned?.transactionStatus
    ? capitalize(unassigned.transactionStatus)
    : null
  const type = unassigned?.transactionType
    ? capitalize(unassigned.transactionType)
    : null
  if (!status && !type) return null
  return { status, type, line: [type, status].filter(Boolean).join(' · ') }
}

export const emptyManualForm = (): ManualFormState => ({
  amount: '',
  type: 'ee_paid',
  source: null,
  reason: AllocationClassification.REFUND_OTHER,
  checkNumber: '',
  date: dayjs().format('YYYY-MM-DD'),
  benefit: null,
  month: null,
  note: '',
})

// ── manual check refund ──────────────────────────────────────────────────────

// Real check numbers are digits. A letter (or a typed `C`) would give the
// transaction an odd name like `CC1042` that search can't find.
const CHECK_NUMBER_PATTERN = /^\d{1,20}$/

export const isValidCheckNumber = (value: string): boolean =>
  CHECK_NUMBER_PATTERN.test(value.trim())

/**
 * The `checkNumber` a manual refund sends: the trimmed number on the Check
 * rail, undefined otherwise — the API rejects a check number on Group ACH.
 */
export const manualRefundCheckNumber = (
  form: Pick<ManualFormState, 'source' | 'checkNumber'>
): string | undefined =>
  form.source === ManualRefundSource.Check ? form.checkNumber.trim() : undefined

/** A refund needs a rail, and the Check rail also needs a valid check number. */
export const isManualRefundRailValid = (
  form: Pick<ManualFormState, 'source' | 'checkNumber'>
): boolean =>
  form.source === ManualRefundSource.GroupAch ||
  (form.source === ManualRefundSource.Check &&
    isValidCheckNumber(form.checkNumber))

// ── success-toast wording ────────────────────────────────────────────────────

interface AllocationOutcome {
  /**
   * Verb the row's button rendered, derived from THIS transaction's own
   * `reason`. Authoritative — it settles refund-ness too, and outranks the
   * amount's sign. Unposted-transaction rows only.
   */
  promisedAction?: PromisedAction
  /**
   * The cell was opened from the Discrepancies tab, whose
   * button says Allocate. Weaker than a promise on purpose: it rules out
   * "Reallocated" but cannot rule on refund-ness, because the row does not
   * know which transaction the operator will work on inside the cell.
   */
  isQueueCell?: boolean
  /** Sign of the source transaction: the only signal a bare cell target has. */
  isRefund: boolean
}

/**
 * Verb for a successful allocate, in the vocabulary spec D11 settles on.
 *
 * Three inputs, in strict order of how much each actually knows:
 *
 *   1. `promisedAction` — the button an unposted-transaction row rendered from
 *      its own `reason`. Outranks the sign, because the sign can lie: a
 *      negative-amount Group ACH reads "Allocate" and is not a refund.
 *   2. `isRefund` — the selected transaction really is negative. D11 says
 *      refunds are POSTED, and this still applies inside a queue-opened cell.
 *   3. `isQueueCell` — placement rather than a re-split.
 *
 * "Reallocated" is therefore reachable only from Benefits, where the operator
 * is re-splitting an allocation already on screen and the word is honest.
 * "Adjusted" is never correct — that is the modal's own name leaking into
 * operator-facing copy.
 */
export const allocationVerb = ({
  promisedAction,
  isQueueCell,
  isRefund,
}: AllocationOutcome): 'Posted' | 'Allocated' | 'Reallocated' => {
  if (promisedAction) return promisedAction === 'Post' ? 'Posted' : 'Allocated'
  if (isRefund) return 'Posted'
  return isQueueCell ? 'Allocated' : 'Reallocated'
}

/**
 * Success toast for an allocate/post: the verb above plus how many
 * benefit-months were written.
 *
 * Counts benefit-months rather than "allocations" — the old wording read as a
 * count of queue rows, and pluralised unconditionally ("across 1 allocations").
 */
export const allocationSuccessMessage = ({
  sliceCount,
  ...outcome
}: AllocationOutcome & { sliceCount: number }): string =>
  sliceCount === 1
    ? `${allocationVerb(outcome)} to 1 benefit-month`
    : `${allocationVerb(outcome)} across ${sliceCount} benefit-months`

/**
 * Success toast for the allocate-with-remainder path, where the operator
 * placed what they could and wrote the leftover off in the same atomic call.
 *
 * Lives here beside its siblings rather than being assembled at the call site:
 * it is the same verb decision, and the sentence frame has to be visible to
 * see that a refund reads "Posted and wrote off …".
 */
export const absorptionSuccessMessage = ({
  remainder,
  ...outcome
}: AllocationOutcome & { remainder: number }): string =>
  `${allocationVerb(outcome)} and wrote off ${fmtMoney(remainder)}`
